import { signal, computed } from '@preact/signals';
import Router, { Route } from 'preact-router';
import { useEffect, useState } from 'preact/hooks';
import { api, type User, type Church } from './services/api';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import { CheckInsPage } from './pages/CheckInsPage';
import { ChurchRegistrationPage } from './pages/ChurchRegistrationPage';
import FamiliesPage from './pages/FamiliesPage';
import { ProfilePage } from './pages/ProfilePage';
import { TeamPage } from './pages/TeamPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { JoinTeamPage } from './pages/JoinTeamPage';
import { Navigation } from './components/Navigation';
import { LoadingSpinner } from './components/LoadingSpinner';

// ── Global auth state ──
export const authState = signal<{
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  church: Church | null;
}>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  church: null,
});

// ── Sidebar collapsed state — persisted to localStorage ──
const _sidebarCollapsed = signal<boolean>(
  typeof localStorage !== 'undefined'
    ? localStorage.getItem('sidebar_collapsed') === 'true'
    : false
);
export const sidebarCollapsed = computed(() => _sidebarCollapsed.value);
export function toggleSidebar() {
  _sidebarCollapsed.value = !_sidebarCollapsed.value;
  localStorage.setItem('sidebar_collapsed', String(_sidebarCollapsed.value));
}
export const SIDEBAR_EXPANDED = 256;
export const SIDEBAR_COLLAPSED = 64;

export function App() {
  // Track desktop vs mobile (md = 768px)
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Auth check on mount
  useEffect(() => {
    async function checkAuth() {
      try {
        if (api.isAuthenticated()) {
          const { user, church } = await api.getCurrentUser();
          authState.value = { isAuthenticated: true, isLoading: false, user, church };
        } else {
          authState.value = { isAuthenticated: false, isLoading: false, user: null, church: null };
        }
      } catch {
        authState.value = { isAuthenticated: false, isLoading: false, user: null, church: null };
      }
    }
    checkAuth();
  }, []);

  // ── Loading splash ──
  if (authState.value.isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--parchment)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }} className="animate-fade-in">
          <div style={{
            width: 56, height: 56,
            background: 'var(--marigold)',
            borderRadius: '13px 13px 13px 3px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 8px 24px rgba(244, 182, 64, 0.32)',
          }}>
            <svg width="28" height="28" fill="none" stroke="var(--ink)" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <LoadingSpinner size="large" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, letterSpacing: '-0.04em', color: 'var(--ink)', margin: '16px 0 4px' }}>
            WeShephard
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-faint)', margin: 0 }}>
            Care Atlas · Authenticating
          </p>
        </div>
      </div>
    );
  }

  // ── Unauthenticated ──
  if (!authState.value.isAuthenticated) {
    return (
      <Router>
        <Route path="/register" component={ChurchRegistrationPage} />
        <Route path="/accept-invite" component={AcceptInvitePage} />
        <Route path="/join-team" component={JoinTeamPage} />
        <Route default component={LoginPage} />
      </Router>
    );
  }

  // ── Authenticated — sidebar layout ──
  const collapsed = sidebarCollapsed.value;
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--parchment)' }}>
      <Navigation />
      <main
        style={{
          minHeight: '100vh',
          paddingLeft: isDesktop ? `${sidebarWidth}px` : 0,
          transition: 'padding-left 300ms var(--ease-out)',
        }}
      >
        {/* Mobile top-bar offset (56px) */}
        <div className="pt-14 md:pt-0">
          <Router>
            <Route path="/" component={Dashboard} />
            <Route path="/dashboard" component={Dashboard} />
            <Route path="/checkins" component={CheckInsPage} />
            <Route path="/families" component={FamiliesPage} />
            <Route path="/team" component={TeamPage} />
            <Route path="/profile" component={ProfilePage} />
            <Route path="/accept-invite" component={AcceptInvitePage} />
            <Route path="/join-team" component={JoinTeamPage} />
            <Route default component={NotFound} />
          </Router>
        </div>
      </main>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ textAlign: 'center' }} className="animate-fade-in">
        <div className="text-gradient" style={{ fontFamily: 'var(--font-display)', fontSize: 80, fontWeight: 700, letterSpacing: '-0.06em', marginBottom: 12 }}>404</div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, letterSpacing: '-0.04em', color: 'var(--ink)', margin: '0 0 8px' }}>Page Not Found</h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--ink-soft)', margin: '0 0 24px' }}>The page you're looking for doesn't exist.</p>
        <a href="/" className="btn-primary btn-lg">Go to Dashboard</a>
      </div>
    </div>
  );
}